/**
 * Fetch Current User from Local Storage UseCase
 * 
 * Loads authenticated user data from SecureStorageWebCrypto as UserStorage entity.
 * Follows the same pattern as syncSecureStorageUseCase - converts from storage entity to domain entity.
 * Mirror of StoreCurrentUserToLocalStorageUseCase - reads what was stored.
 * 
 * Used to populate Redux cache after app initialization.
 */

import { SecureStorageWebCrypto } from '@/data/storage/local/secureStorageWebCrypto';
import type { UserEntity } from '@/domain/entities/user/userEntity';
import type { UserStorage } from '@/domain/entities/secureStorage/secureStorageEntities';
import { UserRemapper } from '@/domain/remappers/user/userRemapper';
import { SECURE_STORAGE_KEYS } from '@/domain/entities/secureStorage/secureStorageKeys';
import { appLogger } from '@/domain/services/analytics/appLogger';

export class FetchCurrentUserFromLocalStorageUseCase {
  private storage: SecureStorageWebCrypto;

  constructor(storage?: SecureStorageWebCrypto) {
    this.storage = storage || SecureStorageWebCrypto.getInstance();
  }

  /**
   * Fetch user from local encrypted storage
   * Converts UserStorage to UserEntity before returning
   * 
   * @returns The user entity or null if not found
   */
  async execute(): Promise<UserEntity | null> {
    try {
      // console.log('[FetchCurrentUserFromLocalStorageUseCase] Loading user from storage');

      // Fetch user storage entity from encrypted storage
      const userStorage = await this.storage.getEncrypted<UserStorage>(SECURE_STORAGE_KEYS.CURRENT_USER);

      if (!userStorage) {
        // console.log('[FetchCurrentUserFromLocalStorageUseCase] No user found in storage');
        return null;
      }

      // Convert storage entity to domain entity (matching syncSecureStorageUseCase pattern)
      const userEntity = UserRemapper.fromStorage(userStorage);

      // console.log('[FetchCurrentUserFromLocalStorageUseCase] User loaded successfully:', userEntity.firebaseId);
      return userEntity;
    } catch (error) {
      appLogger.error('Failed to load user', 'FetchCurrentUserFromLocalStorageUseCase.execute', { error: error as Error });
      return null;
    }
  }
}

// Export singleton instance
export const fetchCurrentUserFromLocalStorageUseCase = new FetchCurrentUserFromLocalStorageUseCase();
